import { ReactElement, useMemo, useCallback, memo } from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';
import classNames from 'classnames';

import { ItemImportStateTypes } from '~globals/types/enums';
import { ItemExtended } from '~services/item/types';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import {
  OrderClientInfo,
  SkillsListInfo,
  OrderPendingStateChip,
  CapabilitiesInfo,
  DataTable,
} from '~components/index';

import { TABLE_KEY_FIELDS } from '../../../../../utils/constants';
import { getDisabledItem } from '../../../../../utils/helpers';

import { useAssistantGenerateRoutesDialogByItemsContext } from '../../../../Context';

import { AssistantGenerateRoutesDialogByItemsContentFormTableContainer } from '../styles';

const AssistantGenerateRoutesDialogByItemsContentFormItemTable =
  (): ReactElement => {
    const { user } = useAppSelector(selectAuth);

    const {
      data: { items: data },
      form: {
        selection: {
          orders: { selectRowsProps },
        },
      },
    } = useAssistantGenerateRoutesDialogByItemsContext();

    const columns = useMemo<ColumnDescription<ItemExtended>[]>(
      () => [
        {
          dataField: 'title',
          text: 'Nombre de contacto',
          headerStyle: { width: 180 },
          formatter: (_cell, row) => (
            <OrderClientInfo
              name={row.title}
              itemTypeId={row.itemTypeId}
              trackingCode={row.trackingCode}
              hasError={
                row.itemImportStateTypeId === ItemImportStateTypes.Error
              }
              duplicated={row.duplicated}
            />
          ),
        },
        {
          dataField: 'tags',
          text: 'Grupo',
          classes: 'truncated-two-lines',
          formatter: (cell) => <span>{cell}</span>,
          headerStyle: { width: 180 },
        },
        {
          dataField: 'skillsNeeded',
          text: 'Cargas especiales',
          classes: 'truncated-two-lines',
          formatter: (cell) => <SkillsListInfo skills={cell} />,
          headerStyle: { width: 100 },
          align: 'center',
        },
        {
          dataField: 'collectItemStateTypeId',
          isDummyField: true,
          text: 'Estado',
          classes: 'truncated-two-lines',
          formatter: (_cell, row) => {
            return (
              <OrderPendingStateChip
                carrierCompanyId={row.carrierCompanyId}
                collectItemStateTypeId={row.collectItemStateTypeId}
              />
            );
          },
          headerStyle: { width: 170 },
          headerAlign: 'center',
          align: 'center',
        },
        {
          dataField: 'unit1',
          text: 'Capacidades',
          formatter: (_cell, row) => (
            <CapabilitiesInfo
              capacity1={row.unit1 * row.amount}
              capacity2={row.unit2 * row.amount}
              numberOfPackages={row.numberOfPackages}
              maximumVisits={null}
              fontSize={12}
              fontWeight="bold"
            />
          ),
          headerStyle: { width: 120 },
        },
      ],
      [],
    );

    const getDisabledRowClases = useCallback(
      (row: ItemExtended): string =>
        classNames({
          'table-row-disabled': getDisabledItem(row, user),
        }),
      [user],
    );

    return (
      <AssistantGenerateRoutesDialogByItemsContentFormTableContainer>
        <DataTable
          loading={false}
          data={data}
          columns={columns}
          keyField={TABLE_KEY_FIELDS.ORDER}
          selectRow={selectRowsProps}
          rowClasses={getDisabledRowClases}
          condensed
        />
      </AssistantGenerateRoutesDialogByItemsContentFormTableContainer>
    );
  };

export default memo(AssistantGenerateRoutesDialogByItemsContentFormItemTable);
